/**
 * Public blog listing + detail + help center listing + help detail.
 * Uses react-helmet-async for SEO. Wired to /content/* backend endpoints.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search as SearchIcon, ChevronRight, Clock } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/layouts/PublicLayout";

const cx = (...c) => c.filter(Boolean).join(" ");

// ---------- Blog listing --------------------------------------------------
export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/content/blog", { params: { limit: 24 } })
      .then(r => { if (alive) { setPosts(r.data || []); setLoading(false); } })
      .catch(e => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, []);
  const categories = Array.from(new Set(posts.map(p => p.category).filter(Boolean)));
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 py-16">
      <Helmet>
        <title>Blog · Gulf Coast Work</title>
        <meta name="description" content="Guides, how-tos, and stories from the Gulf Coast Work marketplace." />
      </Helmet>
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">The GCW Journal</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-[#071b22]">Guides, how-tos, and stories from the Gulf.</h1>
      <p className="mt-3 max-w-2xl text-[#252A34]/75">Hiring guides for customers, business advice for providers, and seasonal checklists tuned to Gulf-Coast weather.</p>
      {categories.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map(c => <span key={c} className="rounded-full bg-slate-100 text-[#252A34] px-3 py-1 text-xs font-medium">{c}</span>)}
        </div>
      )}
      {error && <div className="mt-8 text-sm text-red-600">{error}</div>}
      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
        {loading && posts.length === 0 && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="blog-skeleton">
              <div className="w-full h-48 bg-slate-100 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
                <div className="h-5 w-3/4 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse" />
              </div>
            </div>
          ))
        )}
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#165a71]/40 hover:shadow-md transition" data-testid={`blog-card-${p.slug}`}>
            {p.hero_image_url && <img src={p.hero_image_url} alt="" className="w-full h-48 object-cover" />}
            <div className="p-5">
              <div className="text-xs text-[#165a71] uppercase font-semibold tracking-wider">{p.category}</div>
              <div className="font-display font-semibold text-[#071b22] mt-1 group-hover:text-[#165a71]">{p.title}</div>
              <div className="text-sm text-[#252A34]/70 mt-2 line-clamp-3">{p.excerpt}</div>
              <div className="mt-4 text-xs text-[#252A34]/60 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.reading_time_min || 3} min read</span>
                {p.published_at && <span>{p.published_at.slice(0, 10)}</span>}
              </div>
            </div>
          </Link>
        ))}
        {!loading && posts.length === 0 && !error && <div className="col-span-full text-center py-24 text-[#252A34]/60 text-sm" data-testid="blog-empty">No posts yet.</div>}
      </div>
    </div>
    </PublicLayout>
  );
}

// ---------- Blog detail ---------------------------------------------------
export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/content/blog/${slug}`).then(r => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);
  if (notFound) return <PublicLayout><NotFoundBlock kind="Blog post" /></PublicLayout>;
  if (!post) return <PublicLayout><div className="max-w-3xl mx-auto py-16 px-4 animate-pulse text-[#252A34]/60">Loading…</div></PublicLayout>;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "author": { "@type": "Organization", "name": post.author_name || "Gulf Coast Work" },
    "datePublished": post.published_at,
    "image": post.hero_image_url,
  };
  return (
    <PublicLayout>
    <article className="max-w-3xl mx-auto px-4 py-16" data-testid={`blog-detail-${post.slug}`}>
      <Helmet>
        <title>{post.seo_title || post.title}</title>
        {post.seo_description && <meta name="description" content={post.seo_description} />}
        <meta property="og:title" content={post.seo_title || post.title} />
        {post.seo_description && <meta property="og:description" content={post.seo_description} />}
        {post.seo_image_url && <meta property="og:image" content={post.seo_image_url} />}
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Link to="/blog" className="text-xs text-[#165a71] font-medium">← Back to Blog</Link>
      {post.category && <div className="mt-4 text-xs text-[#165a71] uppercase font-semibold tracking-wider">{post.category}</div>}
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-[#071b22]">{post.title}</h1>
      {post.excerpt && <p className="mt-3 text-lg text-[#252A34]/75">{post.excerpt}</p>}
      <div className="mt-6 text-xs text-[#252A34]/60 flex items-center gap-3">
        <span>{post.author_name}</span>
        <span>·</span>
        <span>{(post.published_at || "").slice(0, 10)}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.reading_time_min} min read</span>
      </div>
      {post.hero_image_url && <img src={post.hero_image_url} alt="" className="mt-8 rounded-2xl w-full" />}
      {post.body && (
        <div className="mt-8 prose prose-slate max-w-none text-[#252A34]/85 leading-relaxed whitespace-pre-line">{post.body}</div>
      )}
      {post.related?.length > 0 && (
        <div className="mt-16 border-t border-slate-200 pt-10">
          <div className="font-display text-xl font-semibold text-[#071b22] mb-4">Keep reading</div>
          <div className="grid gap-4 md:grid-cols-3">
            {post.related.map(r => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="rounded-2xl border border-slate-200 p-4 hover:border-[#165a71]/40 hover:shadow-sm">
                <div className="font-display font-semibold text-[#071b22] line-clamp-2">{r.title}</div>
                <div className="text-sm text-[#252A34]/70 mt-2 line-clamp-2">{r.excerpt}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
    </PublicLayout>
  );
}

// ---------- Help center listing ------------------------------------------
export function HelpCenterPage() {
  const [q, setQ] = useState("");
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/content/help", { params: { q: q || undefined } }).then(r => setArticles(r.data || []));
  }, [q]);
  const groups = {};
  articles.forEach(a => {
    const k = a.category || "General";
    (groups[k] = groups[k] || []).push(a);
  });
  return (
    <PublicLayout>
    <div className="max-w-6xl mx-auto px-4 py-16">
      <Helmet>
        <title>Help Center · Gulf Coast Work</title>
        <meta name="description" content="Answers, guides, and how-tos for customers and providers on Gulf Coast Work." />
      </Helmet>
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Help center</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-[#071b22]">How can we help?</h1>
      <div className="mt-6 relative max-w-lg">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the help center…" className="w-full pl-10 pr-3 py-3 text-sm rounded-full border border-slate-200 focus:border-[#165a71] focus:outline-none" data-testid="help-search" />
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="help-groups">
        {Object.entries(groups).map(([cat, arts]) => (
          <div key={cat} className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="font-display font-semibold text-[#071b22] mb-3">{cat}</div>
            <ul className="space-y-2">
              {arts.map(a => (
                <li key={a.id}>
                  <Link to={`/help/${a.slug}`} className="text-sm text-[#165a71] hover:underline flex items-center justify-between gap-2 group" data-testid={`help-article-${a.slug}`}>
                    <span className="line-clamp-1">{a.title}</span>
                    <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
    </PublicLayout>
  );
}

// ---------- Help article detail ------------------------------------------
export function HelpArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/content/help/${slug}`).then(r => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);
  if (notFound) return <PublicLayout><NotFoundBlock kind="Help article" /></PublicLayout>;
  if (!article) return <PublicLayout><div className="max-w-3xl mx-auto py-16 px-4 animate-pulse text-[#252A34]/60">Loading…</div></PublicLayout>;
  return (
    <PublicLayout>
    <article className="max-w-3xl mx-auto px-4 py-16" data-testid={`help-detail-${article.slug}`}>
      <Helmet>
        <title>{article.seo_title || article.title}</title>
        {article.seo_description && <meta name="description" content={article.seo_description} />}
      </Helmet>
      <Link to="/help" className="text-xs text-[#165a71] font-medium">← Back to Help</Link>
      {article.category && <div className="mt-4 text-xs text-[#165a71] uppercase font-semibold tracking-wider">{article.category}</div>}
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">{article.title}</h1>
      {article.body && (
        <div className="mt-8 prose prose-slate max-w-none text-[#252A34]/85 leading-relaxed whitespace-pre-line">{article.body}</div>
      )}
    </article>
    </PublicLayout>
  );
}

function NotFoundBlock({ kind }) {
  return (
    <div className="max-w-lg mx-auto py-24 text-center px-4">
      <div className="text-xs uppercase tracking-widest text-[#165a71]">404</div>
      <h1 className="mt-2 font-display text-3xl font-semibold text-[#071b22]">{kind} not found</h1>
    </div>
  );
}
